import React, { useState, useEffect } from "react";
import axios from "axios";
import { ACCESS_KEY } from "../config";
import "./CommissionSetting.css";

const DEFAULT_SLABS = [
  { minHosts: 1, maxHosts: 5, commissionPercent: 5, minTargetAmount: 500000 },
  { minHosts: 6, maxHosts: 10, commissionPercent: 10, minTargetAmount: 1000000 },
  { minHosts: 11, maxHosts: 20, commissionPercent: 12, minTargetAmount: 2000000 },
  { minHosts: 21, maxHosts: 9999, commissionPercent: 15, minTargetAmount: 5000000 }
];

const CommissionSetting = () => {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    agencySlabs: DEFAULT_SLABS,
    adminCommissionPercent: 0,
    bdSetting: { type: "flat", flatValue: 0 },
    countryHeadCommissionPercent: 0
  });

  useEffect(() => {
    const fetchSettings = () => {
      setLoading(true);
      axios
        .get("https://admin.butterlive.co.in/management/commission/getCommissionSetting", {
          headers: { key: ACCESS_KEY }
        })
        .then((response) => {
          if (response.data.status && response.data.data) {
            const serverData = response.data.data;
            if (!serverData.agencySlabs || serverData.agencySlabs.length === 0) {
              serverData.agencySlabs = DEFAULT_SLABS;
            }
            setSettings(serverData);
          }
        })
        .catch((error) => console.error("Error fetching settings:", error))
        .finally(() => setLoading(false));
    };
    fetchSettings();
  }, []);

  const handleSlabChange = (index, field, value) => {
    const updatedSlabs = [...settings.agencySlabs];
    updatedSlabs[index][field] = Number(value);
    setSettings({ ...settings, agencySlabs: updatedSlabs });
  };

  const handleGlobalChange = (field, value) => {
    setSettings({ ...settings, [field]: Number(value) });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    axios
      .post("https://admin.butterlive.co.in/management/commission/updateCommissionSetting", settings, {
        headers: { key: ACCESS_KEY }
      })
      .then((response) => {
        if (response.data.status) {
          alert("Commission settings updated successfully!");
        } else {
          alert(response.data.message || "Failed to update settings.");
        }
      })
      .catch((error) => {
        console.error("Error saving settings:", error);
        alert("Something went wrong while saving.");
      })
      .finally(() => setLoading(false));
  };

  if (loading && !settings._id) {
    return <div className="dark-page-loader">Loading configuration settings...</div>;
  }

  return (
    <div className="commission-page-container">
      <form onSubmit={handleSubmit}>
        
        {/* TOP ROW: 3 CARDS SIDE BY SIDE (ALL IDENTICAL INPUTS) */}
        <div className="commission-dashboard-grid">
          
          {/* CARD 1: Country Head */}
          <div className="setup-card">
            <div className="card-header">
              <h3 className="card-title">Country Head Charges</h3>
              <span className="info-icon">ⓘ</span>
            </div>
            <div className="form-group">
              <label className="input-label">Country Head Commission (%)</label>
              <input
                type="number"
                className="dark-form-input"
                value={settings.countryHeadCommissionPercent}
                onChange={(e) => handleGlobalChange("countryHeadCommissionPercent", e.target.value)}
                required
              />
            </div>
          </div>

          {/* CARD 2: Admin Setup */}
          <div className="setup-card">
            <div className="card-header">
              <h3 className="card-title">Admin Setup</h3>
              <span className="info-icon">ⓘ</span>
            </div>
            <div className="form-group">
              <label className="input-label">Admin Commission Share (%)</label>
              <input
                type="number"
                className="dark-form-input"
                value={settings.adminCommissionPercent}
                onChange={(e) => handleGlobalChange("adminCommissionPercent", e.target.value)}
                required
              />
            </div>
          </div>

          {/* CARD 3: BD Admin Setup (Exactly like others now) */}
          <div className="setup-card">
            <div className="card-header">
              <h3 className="card-title">BD Admin Charges</h3>
              <span className="info-icon">ⓘ</span>
            </div>
            <div className="form-group">
              <label className="input-label">BD Admin Flat Commission (%)</label>
              <input
                type="number"
                className="dark-form-input"
                value={settings.bdSetting?.flatValue || 0}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    bdSetting: { ...settings.bdSetting, flatValue: Number(e.target.value) }
                  })
                }
                required
              />
            </div>
          </div>

        </div>

        {/* BOTTOM ROW: WIDE CARD FOR AGENCY SLABS */}
        <div className="setup-card wide-card">
          <div className="card-header">
            <h3 className="card-title">Agency Commission Slabs Setup</h3>
            <span className="info-icon">ⓘ</span>
          </div>

          <div className="slabs-wrapper">
            {settings.agencySlabs && settings.agencySlabs.map((slab, index) => (
              <div className="slab-row-grid" key={index}>
                <div className="slab-badge">Slab {index + 1}</div>
                
                <div className="form-group">
                  <label className="input-label">Min Hosts</label>
                  <input
                    type="number"
                    className="dark-form-input"
                    value={slab.minHosts}
                    onChange={(e) => handleSlabChange(index, "minHosts", e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="input-label">Max Hosts</label>
                  <input
                    type="number"
                    className="dark-form-input"
                    value={slab.maxHosts}
                    onChange={(e) => handleSlabChange(index, "maxHosts", e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="input-label">Commission (%)</label>
                  <input
                    type="number"
                    className="dark-form-input"
                    value={slab.commissionPercent}
                    onChange={(e) => handleSlabChange(index, "commissionPercent", e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="input-label">Min Monthly Target (Coins)</label>
                  <input
                    type="number"
                    className="dark-form-input"
                    value={slab.minTargetAmount}
                    onChange={(e) => handleSlabChange(index, "minTargetAmount", e.target.value)}
                    required
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="card-action-footer">
            <button type="submit" className="pink-submit-btn" disabled={loading}>
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>

      </form>
    </div>
  );
};

export default CommissionSetting;
